import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  HomeIcon,
  UsersIcon,
  FolderIcon,
  ClipboardDocumentListIcon,
  CalendarIcon,
  DocumentTextIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import { useUIStore, useAuthStore } from '../../stores';
import { Avatar } from '../ui';

const mainNavigation = [
  { name: 'nav.dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'nav.clients', href: '/clients', icon: UsersIcon },
  { name: 'nav.projects', href: '/projects', icon: FolderIcon },
  { name: 'nav.tasks', href: '/tasks', icon: ClipboardDocumentListIcon }
];

const secondaryNavigation = [
  { name: 'nav.calendar', href: '/calendar', icon: CalendarIcon },
  { name: 'nav.invoices', href: '/invoices', icon: DocumentTextIcon },
  { name: 'nav.reports', href: '/reports', icon: ChartBarIcon },
  { name: 'nav.chat', href: '/chat', icon: ChatBubbleLeftRightIcon }
];

export default function Sidebar() {
  const { t } = useTranslation();
  const { sidebarOpen, mobileMenuOpen, closeMobileMenu } = useUIStore();
  const { user } = useAuthStore();
  const location = useLocation();

  const isActive = (href) => {
    if (href === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(href);
  };

  const sidebarContent = (
    <>
      {/* Header */}
      <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-3">
          {sidebarOpen && (
            <img src={'/proctm_logo_blue.png'} alt="logo" className="w-auto h-12" />
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-6 overflow-y-auto scrollbar-thin">
        {/* Main Navigation */}
        <div>
          {!sidebarOpen && (
            <div className="px-3 mb-2">
              <div className="h-px bg-gray-200"></div>
            </div>
          )}
          <div className="space-y-1">
            {mainNavigation
              .filter(item => {
                // Filter navigation based on user role
                if (user?.role === 'client') {
                  // Clients only see Dashboard and Projects
                  return item.href === '/dashboard' || item.href === '/projects';
                }
                return true;
              })
              .map((item) => {
                const active = isActive(item.href);
                return (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    onClick={closeMobileMenu}
                    className={({ isActive: navActive }) =>
                      `group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${navActive || active
                        ? 'bg-primary text-white shadow-sm'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                      }`
                    }
                    title={!sidebarOpen ? t(item.name) : ''}
                  >
                    <item.icon className={`w-5 h-5 flex-shrink-0 ${isActive(item.href) ? 'text-white' : ''}`} />
                    {sidebarOpen && <span>{t(item.name)}</span>}
                  </NavLink>
                );
              })}
          </div>
        </div>

        {/* Secondary Navigation */}
        {sidebarOpen && (
          <div>
            <div className="px-3 mb-2">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                {t('nav.secondary') || 'More'}
              </p>
            </div>
            <div className="space-y-1">
              {secondaryNavigation
                .filter(item => {
                  // Filter navigation based on user role
                  if (user?.role === 'client') {
                    // Clients don't see secondary navigation items and tasks and clients
                    return false;
                  }
                  return true;
                })
                .map((item) => {
                  const active = isActive(item.href);
                  return (
                    <NavLink
                      key={item.href}
                      to={item.href}
                      onClick={closeMobileMenu}
                      className={({ isActive: navActive }) =>
                        `group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${navActive || active
                          ? 'bg-primary/10 text-primary border-l-2 border-primary'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                        }`
                      }
                    >
                      <item.icon className="w-5 h-5 flex-shrink-0" />
                      <span>{t(item.name)}</span>
                    </NavLink>
                  );
                })}
            </div>
          </div>
        )}
      </nav>

      {/* Footer */}
      <div className="border-t border-gray-200 bg-gray-50">
        {/* Settings */}
        <div className="p-3">
          <NavLink
            to="/settings"
            onClick={closeMobileMenu}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${isActive
                ? 'bg-primary/10 text-primary'
                : 'text-gray-600 hover:bg-gray-100'
              }`
            }
            title={!sidebarOpen ? t('nav.settings') : ''}
          >
            <Cog6ToothIcon className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span>{t('nav.settings')}</span>}
          </NavLink>
        </div>

        {/* User Profile Section */}
        {sidebarOpen && user && (
          <div className="px-3 pb-3">
            <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer group">
              <Avatar
                src={user.avatar ? (user.avatar.startsWith('http') ? user.avatar : process.env.REACT_APP_SOCKET_URL + user.avatar) : null}
                name={`${user.firstName} ${user.lastName}`}
                size="sm"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user.firstName} {user.lastName}
                </p>
                <p className="text-xs text-gray-500 truncate">{user.email}</p>
              </div>
            </div>
          </div>
        )}

        {/* Collapsed User Avatar */}
        {!sidebarOpen && user && (
          <div className="p-3 flex justify-center">
            <NavLink
              to="/settings"
              onClick={closeMobileMenu}
              className="flex items-center justify-center"
              title={`${user.firstName} ${user.lastName}`}
            >
              <Avatar
                src={user.avatar ? (user.avatar.startsWith('http') ? user.avatar : process.env.REACT_APP_SOCKET_URL + user.avatar) : null}
                name={`${user.firstName} ${user.lastName}`}
                size="sm"
              />
            </NavLink>
          </div>
        )}
      </div>
    </>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside
        className={`sidebar hidden lg:flex flex-col bg-white border-r border-gray-200 transition-all duration-300 ease-in-out ${sidebarOpen ? 'w-64' : 'w-20'
          }`}
      >
        {sidebarContent}
      </aside>

      {/* Mobile Sidebar */}
      {mobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/50 lg:hidden"
            onClick={closeMobileMenu}
          />
          <aside className="sidebar flex flex-col lg:hidden w-64 z-50">
            {sidebarContent}
          </aside>
        </>
      )}
    </>
  );
}